import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { setSidebarOpen } from '../../../store/slices/uiSlice';
import { useAuth } from '../../../hooks/useAuth';
// import { getDynamicNavigationItems } from './NavigationItems';

import { getDynamicNavigationItems } from '../Sidebar/NavigationItems';
import ThemeToggle from '../ThemeToggle';

const DRAWER_WIDTH = 280;

const groupItemsByCategory = (items) => {
  return items.reduce((acc, item) => {
    const category = item.category || 'Main';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, role } = useAuth();
  const { sidebarOpen, sidebarPermanent } = useSelector((state) => state.ui);

  const navigationItems = useMemo(() => getDynamicNavigationItems(role), [role]);
  const groupedItems = useMemo(() => groupItemsByCategory(navigationItems), [navigationItems]);

  const handleNavigate = (path) => {
    navigate(path);
    if (!sidebarPermanent) {
      dispatch(setSidebarOpen(false));
    }
  };

  const renderListItems = (items) => {
    return items.map((item) => {
      const Icon = item.icon;
      const isActive = location.pathname.startsWith(item.path);

      return (
        <ListItemButton
          key={item.id}
          selected={isActive}
          onClick={() => handleNavigate(item.path)}
          sx={{
            borderRadius: 1,
            mx: 2,
            my: 0.5,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
              '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
            },
          }}
        >
          <ListItemIcon><Icon /></ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      );
    });
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Dashboard
        </Typography>
        {user && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Welcome, {user.username || 'User'}
          </Typography>
        )}
      </Box>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {Object.entries(groupedItems).map(([category, items]) => (
          <Box key={category}>
            <Typography variant="overline" sx={{ px: 3, display: 'block', color: 'text.secondary' }}>
              {category}
            </Typography>
            <List disablePadding>{renderListItems(items)}</List>
          </Box>
        ))}
      </Box>
      <Box sx={{ p: 2 }}>
        <Divider sx={{ mb: 2 }}/>
        <ThemeToggle showLabel size="small" />
        <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {user?.email}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            Role: {role}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
      <Drawer
        variant={sidebarPermanent ? "permanent" : "temporary"}
        open={sidebarPermanent || sidebarOpen}
        onClose={() => dispatch(setSidebarOpen(false))}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: { md: 'none' },
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;

// // ====================================
// // src/components/common/Sidebar/Sidebar.jsx
// // ====================================
// import React from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { useSelector, useDispatch } from 'react-redux';
// import {
//   Drawer,
//   List,
//   ListItem,
//   ListItemButton,
//   ListItemIcon,
//   ListItemText,
//   Box,
//   Typography,
//   Divider,
//   useTheme,
//   useMediaQuery,
//   Chip,
//   Tooltip,
// } from '@mui/material';
// import { setSidebarOpen } from '../../../store/slices/uiSlice';
// import { useAuth } from '../../../hooks/useAuth';
// import { useNavigation } from '../../../hooks/useNavigation';
// import { USER_ROLES, ROUTES } from '../../../utils/constants';
// import ThemeToggle from '../ThemeToggle';

// const DRAWER_WIDTH = 280;

// const Sidebar = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const dispatch = useDispatch();
//   const { user, role } = useAuth();
//   const { 
//     navigationItems, 
//     categorizedItems, 
//     roleInfo, 
//     isActiveRoute, 
//     canAccess,
//     navigationStats 
//   } = useNavigation();
  
//   const { sidebarOpen, sidebarPermanent } = useSelector((state) => state.ui);
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

//   const handleNavigate = (path) => {
//     if (canAccess(path)) {
//       navigate(path);
//       if (isMobile) {
//         dispatch(setSidebarOpen(false));
//       }
//     }
//   };

//   const handleClose = () => {
//     dispatch(setSidebarOpen(false));
//   };

//   const groupedItems = categorizedItems;

//   const renderNavigationGroup = (categoryName, items) => {
//     if (!items || items.length === 0) return null;

//     const categoryLabels = {
//       main: 'Main',
//       business: 'Business',
//       finance: 'Financial',
//       admin: 'Administration',
//       settings: 'Settings',
//     };

//     return (
//       <Box key={categoryName}>
//         {categoryName !== 'main' && (
//           <Typography
//             variant="overline"
//             sx={{
//               px: 3,
//               py: 1,
//               display: 'block',
//               color: 'text.secondary',
//               fontSize: '0.75rem',
//               fontWeight: 600,
//               letterSpacing: '0.1em',
//             }}
//           >
//             {categoryLabels[categoryName] || categoryName}
//           </Typography>
//         )}
        
//         {items.map((item) => {
//           const Icon = item.icon;
//           const isActive = isActiveRoute(item.path);
//           const hasAccess = canAccess(item.path);

//           return (
//             <ListItem key={item.id} disablePadding sx={{ px: 1 }}>
//               <Tooltip 
//                 title={!hasAccess ? "Access restricted" : item.description || item.label}
//                 placement="right"
//               >
//                 <ListItemButton
//                   onClick={() => handleNavigate(item.path)}
//                   selected={isActive}
//                   disabled={!hasAccess}
//                   sx={{
//                     borderRadius: 1,
//                     mx: 1,
//                     minHeight: 48,
//                     opacity: hasAccess ? 1 : 0.5,
//                     '&.Mui-selected': {
//                       bgcolor: 'primary.main',
//                       color: 'primary.contrastText',
//                       '&:hover': {
//                         bgcolor: 'primary.dark',
//                       },
//                       '& .MuiListItemIcon-root': {
//                         color: 'primary.contrastText',
//                       },
//                     },
//                     '&:hover': {
//                       bgcolor: isActive ? 'primary.dark' : 'action.hover',
//                     },
//                     '&.Mui-disabled': {
//                       opacity: 0.3,
//                     },
//                   }}
//                 >
//                   <ListItemIcon
//                     sx={{
//                       minWidth: 40,
//                       color: isActive ? 'inherit' : 'action.active',
//                     }}
//                   >
//                     <Icon />
//                   </ListItemIcon>
//                   <ListItemText
//                     primary={item.label}
//                     secondary={item.badge ? (
//                       <Chip 
//                         label={item.badge} 
//                         size="small" 
//                         color="primary" 
//                         variant="outlined"
//                         sx={{ mt: 0.5 }}
//                       />
//                     ) : null}
//                     primaryTypographyProps={{
//                       fontSize: '0.9rem',
//                       fontWeight: isActive ? 600 : 400,
//                     }}
//                   />
//                   {item.isNew && (
//                     <Chip
//                       label="NEW"
//                       size="small"
//                       color="success"
//                       sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
//                     />
//                   )}
//                 </ListItemButton>
//               </Tooltip>
//             </ListItem>
//           );
//         })}
        
//         {Object.keys(groupedItems).indexOf(categoryName) < Object.keys(groupedItems).length - 1 && (
//           <Divider sx={{ mx: 2, my: 1 }} />
//         )}
//       </Box>
//     );
//   };

//   const drawerContent = (
//     <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//       <Box
//         sx={{
//           p: 2,
//           borderBottom: 1,
//           borderColor: 'divider',
//           textAlign: 'center',
//         }}
//       >
//         <Typography
//           variant="h5"
//           component="div"
//           sx={{
//             fontWeight: 'bold',
//             color: 'primary.main',
//           }}
//         >
//           Dashboard
//         </Typography>
//         {user && (
//           <>
//             <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
//               Welcome, {user.username || user.name}
//             </Typography>
//             <Chip
//               label={roleInfo?.label || roleInfo?.welcome}
//               color={roleInfo?.color}
//               size="small"
//               variant="outlined"
//               sx={{ mt: 1 }}
//             />
//           </>
//         )}
//       </Box>

//       <Box sx={{ flex: 1, overflow: 'auto' }}>
//         <List sx={{ pt: 1 }}>
//           {Object.entries(groupedItems).map(([category, items]) =>
//             renderNavigationGroup(category, items)
//           )}
//         </List>

//         {role && (
//           <Box sx={{ px: 2, py: 1 }}>
//             <Typography
//               variant="caption"
//               color="text.secondary"
//               sx={{ display: 'block', mb: 1 }}
//             >
//               Quick Access ({navigationStats?.totalItems || 0} items available)
//             </Typography>
//           </Box>
//         )}
//       </Box>

//       <Box sx={{ mt: 'auto' }}>
//         <Divider sx={{ mx: 2 }} />
//         <Box sx={{ p: 2 }}>
//           <Typography
//             variant="body2"
//             color="text.secondary"
//             sx={{ mb: 1, fontWeight: 500 }}
//           >
//             Settings
//           </Typography>
//           <ThemeToggle showLabel size="small" />
          
//           <Box sx={{ mt: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
//             <Typography
//               variant="caption"
//               color="text.secondary"
//               sx={{ display: 'block' }}
//             >
//               Logged in as: {user?.email}
//             </Typography>
//             <Typography
//               variant="caption"
//               color="text.primary"
//               sx={{ display: 'block', fontWeight: 500 }}
//             >
//               Access Level: {roleInfo?.label || roleInfo?.welcome}
//             </Typography>
//             {user?.organization && (
//               <Typography
//                 variant="caption"
//                 color="text.secondary"
//                 sx={{ display: 'block' }}
//               >
//                 Org: {user.organization}
//               </Typography>
//             )}
//           </Box>
//         </Box>
//       </Box>
//     </Box>
//   );

//   return (
//     <Box
//       component="nav"
//       sx={{
//         width: { md: sidebarPermanent ? DRAWER_WIDTH : 0 },
//         flexShrink: { md: 0 },
//       }}
//     >
//       <Drawer
//         variant="temporary"
//         open={sidebarOpen}
//         onClose={handleClose}
//         ModalProps={{
//           keepMounted: true,
//         }}
//         sx={{
//           display: { xs: 'block', md: sidebarPermanent ? 'none' : 'block' },
//           '& .MuiDrawer-paper': {
//             boxSizing: 'border-box',
//             width: DRAWER_WIDTH,
//             borderRight: 1,
//             borderColor: 'divider',
//           },
//         }}
//       >
//         {drawerContent}
//       </Drawer>

//       <Drawer
//         variant="permanent"
//         sx={{
//           display: { xs: 'none', md: sidebarPermanent ? 'block' : 'none' },
//           '& .MuiDrawer-paper': {
//             boxSizing: 'border-box',
//             width: DRAWER_WIDTH,
//             borderRight: 1,
//             borderColor: 'divider',
//             position: 'fixed',
//             height: '100vh',
//             top: 0,
//             left: 0,
//             zIndex: theme.zIndex.drawer,
//           },
//         }}
//         open
//       >
//         {drawerContent}
//       </Drawer>
//     </Box>
//   );
// };

// export default Sidebar;

// // src/components/common/Sidebar/Sidebar.jsx
// import React from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { useSelector, useDispatch } from 'react-redux';
// import {
//   Drawer,
//   List,
//   ListItem,
//   ListItemButton,
//   ListItemIcon,
//   ListItemText,
//   Box,
//   Typography,
//   Divider,
//   useTheme,
//   useMediaQuery,
// } from '@mui/material';
// import { setSidebarOpen } from '../../../store/slices/uiSlice';
// import { useAuth } from '../../../hooks/useAuth';
// import { navigationItems } from './NavigationItems';
// import ThemeToggle from '../ThemeToggle';
// import { ROUTES } from '../../../utils/constants';

// const DRAWER_WIDTH = 280;

// const Sidebar = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const dispatch = useDispatch();
//   const { user, role } = useAuth();
  
//   const { sidebarOpen, sidebarPermanent } = useSelector((state) => state.ui);
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

//   const handleNavigate = (path) => {
//     navigate(path);
//     // Close sidebar on mobile after navigation
//     if (isMobile) {
//       dispatch(setSidebarOpen(false));
//     }
//   };

//   const handleClose = () => {
//     dispatch(setSidebarOpen(false));
//   };

//   const isActiveRoute = (path) => {
//     if (path === ROUTES.DASHBOARD) {
//       return location.pathname === path;
//     }
//     return location.pathname.startsWith(path);
//   };

//   const drawerContent = (
//     <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//       {/* Logo/Brand Section */}
//       <Box
//         sx={{
//           p: 2,
//           borderBottom: 1,
//           borderColor: 'divider',
//           textAlign: 'center',
//         }}
//       >
//         <Typography
//           variant="h5"
//           component="div"
//           sx={{
//             fontWeight: 'bold',
//             color: 'primary.main',
//           }}
//         >
//           Dashboard
//         </Typography>
//         {user && (
//           <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
//             Welcome, {user.username || user.name}
//           </Typography>
//         )}
//       </Box>

//       {/* Navigation Items */}
//       <Box sx={{ flex: 1, overflow: 'auto' }}>
//         <List sx={{ pt: 1 }}>
//           {navigationItems.map((item) => {
//             const Icon = item.icon;
//             const isActive = isActiveRoute(item.path);

//             return (
//               <ListItem key={item.id} disablePadding sx={{ px: 1 }}>
//                 <ListItemButton
//                   onClick={() => handleNavigate(item.path)}
//                   selected={isActive}
//                   sx={{
//                     borderRadius: 1,
//                     mx: 1,
//                     minHeight: 48,
//                     '&.Mui-selected': {
//                       bgcolor: 'primary.main',
//                       color: 'primary.contrastText',
//                       '&:hover': {
//                         bgcolor: 'primary.dark',
//                       },
//                       '& .MuiListItemIcon-root': {
//                         color: 'primary.contrastText',
//                       },
//                     },
//                     '&:hover': {
//                       bgcolor: isActive ? 'primary.dark' : 'action.hover',
//                     },
//                   }}
//                 >
//                   <ListItemIcon
//                     sx={{
//                       minWidth: 40,
//                       color: isActive ? 'inherit' : 'action.active',
//                     }}
//                   >
//                     <Icon />
//                   </ListItemIcon>
//                   <ListItemText
//                     primary={item.label}
//                     primaryTypographyProps={{
//                       fontSize: '0.9rem',
//                       fontWeight: isActive ? 600 : 400,
//                     }}
//                   />
//                 </ListItemButton>
//               </ListItem>
//             );
//           })}
//         </List>
//       </Box>

//       {/* Theme Toggle at Bottom (inside User Management section) */}
//       <Box sx={{ mt: 'auto' }}>
//         <Divider sx={{ mx: 2 }} />
//         <Box sx={{ p: 2 }}>
//           <Typography
//             variant="body2"
//             color="text.secondary"
//             sx={{ mb: 1, fontWeight: 500 }}
//           >
//             Settings
//           </Typography>
//           <ThemeToggle showLabel size="small" />
//           {role && (
//             <Typography
//               variant="caption"
//               color="text.secondary"
//               sx={{ display: 'block', mt: 1 }}
//             >
//               Role: {role.charAt(0).toUpperCase() + role.slice(1)}
//             </Typography>
//           )}
//         </Box>
//       </Box>
//     </Box>
//   );

//   return (
//     <Box
//       component="nav"
//       sx={{
//         width: { md: sidebarPermanent ? DRAWER_WIDTH : 0 },
//         flexShrink: { md: 0 },
//       }}
//     >
//       {/* Mobile drawer */}
//       <Drawer
//         variant="temporary"
//         open={sidebarOpen}
//         onClose={handleClose}
//         ModalProps={{
//           keepMounted: true, // Better open performance on mobile
//         }}
//         sx={{
//           display: { xs: 'block', md: sidebarPermanent ? 'none' : 'block' },
//           '& .MuiDrawer-paper': {
//             boxSizing: 'border-box',
//             width: DRAWER_WIDTH,
//             borderRight: 1,
//             borderColor: 'divider',
//           },
//         }}
//       >
//         {drawerContent}
//       </Drawer>

//       {/* Desktop permanent drawer - FIXED POSITIONING */}
//       <Drawer
//         variant="permanent"
//         sx={{
//           display: { xs: 'none', md: sidebarPermanent ? 'block' : 'none' },
//           '& .MuiDrawer-paper': {
//             boxSizing: 'border-box',
//             width: DRAWER_WIDTH,
//             borderRight: 1,
//             borderColor: 'divider',
//             position: 'fixed', // Changed from 'relative' to 'fixed'
//             height: '100vh',
//             top: 0,
//             left: 0,
//             zIndex: theme.zIndex.drawer,
//           },
//         }}
//         open
//       >
//         {drawerContent}
//       </Drawer>
//     </Box>
//   );
// };

// export default Sidebar;

// // src/components/common/Sidebar/Sidebar.jsx
// import React from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { useSelector, useDispatch } from 'react-redux';
// import {
//   Drawer,
//   List,
//   ListItem,
//   ListItemButton,
//   ListItemIcon,
//   ListItemText,
//   Box,
//   Typography,
//   Divider,
//   useTheme,
//   useMediaQuery,
// } from '@mui/material';
// import { setSidebarOpen } from '../../../store/slices/uiSlice';
// import { useAuth } from '../../../hooks/useAuth';
// import { navigationItems } from './NavigationItems';
// import ThemeToggle from '../ThemeToggle';
// import { ROUTES } from '../../../utils/constants';

// const DRAWER_WIDTH = 280;

// const Sidebar = () => {
//   const theme = useTheme();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const dispatch = useDispatch();
//   const { user, role } = useAuth();
  
//   const { sidebarOpen, sidebarPermanent } = useSelector((state) => state.ui);
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

//   const handleNavigate = (path) => {
//     navigate(path);
//     // Close sidebar on mobile after navigation
//     if (isMobile) {
//       dispatch(setSidebarOpen(false));
//     }
//   };

//   const handleClose = () => {
//     dispatch(setSidebarOpen(false));
//   };

//   const isActiveRoute = (path) => {
//     if (path === ROUTES.DASHBOARD) {
//       return location.pathname === path;
//     }
//     return location.pathname.startsWith(path);
//   };

//   const drawerContent = (
//     <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
//       {/* Logo/Brand Section */}
//       <Box
//         sx={{
//           p: 2,
//           borderBottom: 1,
//           borderColor: 'divider',
//           textAlign: 'center',
//         }}
//       >
//         <Typography
//           variant="h5"
//           component="div"
//           sx={{
//             fontWeight: 'bold',
//             color: 'primary.main',
//           }}
//         >
//           Dashboard
//         </Typography>
//         {user && (
//           <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
//             Welcome, {user.username || user.name}
//           </Typography>
//         )}
//       </Box>

//       {/* Navigation Items */}
//       <Box sx={{ flex: 1, overflow: 'auto' }}>
//         <List sx={{ pt: 1 }}>
//           {navigationItems.map((item) => {
//             const Icon = item.icon;
//             const isActive = isActiveRoute(item.path);

//             return (
//               <ListItem key={item.id} disablePadding sx={{ px: 1 }}>
//                 <ListItemButton
//                   onClick={() => handleNavigate(item.path)}
//                   selected={isActive}
//                   sx={{
//                     borderRadius: 1,
//                     mx: 1,
//                     minHeight: 48,
//                     '&.Mui-selected': {
//                       bgcolor: 'primary.main',
//                       color: 'primary.contrastText',
//                       '&:hover': {
//                         bgcolor: 'primary.dark',
//                       },
//                       '& .MuiListItemIcon-root': {
//                         color: 'primary.contrastText',
//                       },
//                     },
//                     '&:hover': {
//                       bgcolor: isActive ? 'primary.dark' : 'action.hover',
//                     },
//                   }}
//                 >
//                   <ListItemIcon
//                     sx={{
//                       minWidth: 40,
//                       color: isActive ? 'inherit' : 'action.active',
//                     }}
//                   >
//                     <Icon />
//                   </ListItemIcon>
//                   <ListItemText
//                     primary={item.label}
//                     primaryTypographyProps={{
//                       fontSize: '0.9rem',
//                       fontWeight: isActive ? 600 : 400,
//                     }}
//                   />
//                 </ListItemButton>
//               </ListItem>
//             );
//           })}
//         </List>
//       </Box>

//       {/* Theme Toggle at Bottom (inside User Management section) */}
//       <Box sx={{ mt: 'auto' }}>
//         <Divider sx={{ mx: 2 }} />
//         <Box sx={{ p: 2 }}>
//           <Typography
//             variant="body2"
//             color="text.secondary"
//             sx={{ mb: 1, fontWeight: 500 }}
//           >
//             Settings
//           </Typography>
//           <ThemeToggle showLabel size="small" />
//           {role && (
//             <Typography
//               variant="caption"
//               color="text.secondary"
//               sx={{ display: 'block', mt: 1 }}
//             >
//               Role: {role.charAt(0).toUpperCase() + role.slice(1)}
//             </Typography>
//           )}
//         </Box>
//       </Box>
//     </Box>
//   );

//   return (
//     <Box
//       component="nav"
//       sx={{
//         width: { md: sidebarPermanent ? DRAWER_WIDTH : 0 },
//         flexShrink: { md: 0 },
//       }}
//     >
//       {/* Mobile drawer */}
//       <Drawer
//         variant="temporary"
//         open={sidebarOpen}
//         onClose={handleClose}
//         ModalProps={{
//           keepMounted: true, // Better open performance on mobile
//         }}
//         sx={{
//           display: { xs: 'block', md: sidebarPermanent ? 'none' : 'block' },
//           '& .MuiDrawer-paper': {
//             boxSizing: 'border-box',
//             width: DRAWER_WIDTH,
//             borderRight: 1,
//             borderColor: 'divider',
//           },
//         }}
//       >
//         {drawerContent}
//       </Drawer>

//       {/* Desktop permanent drawer */}
//       <Drawer
//         variant="permanent"
//         sx={{
//           display: { xs: 'none', md: sidebarPermanent ? 'block' : 'none' },
//           '& .MuiDrawer-paper': {
//             boxSizing: 'border-box',
//             width: DRAWER_WIDTH,
//             borderRight: 1,
//             borderColor: 'divider',
//             position: 'relative',
//             height: '100vh',
//           },
//         }}
//         open
//       >
//         {drawerContent}
//       </Drawer>
//     </Box>
//   );
// };

// export default Sidebar;